import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Drawer,
  IconButton,
  TextField,
  Collapse,
  Chip,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import LoyaltyIcon from '@material-ui/icons/Loyalty';
import NotesIcon from '@material-ui/icons/Notes';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import useApi from '../../../utils/useApi';
import UIDialogConfirm from '../../UI/Dialog/Confirm/UIDialogConfirm';
import { formattedCurrency } from '../../../utils/utils';
import SaleMobileQuantityModal from './SaleMobileQuantityModal';
import ClientCreateModal from '../../Client/ClientCreateModal';
import './saleMobile.css';

// Helper para normalizar acentuação na busca
const normalizeText = (text) => {
  if (!text) return '';
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const SaleMobileCartDrawer = ({
  open,
  onClose,
  listItens = [],
  cartValues = {},
  manualDiscount = 0,
  onEditItem,
  onRemoveItem,
  onSelectCustomer,
  onSelectPromotion,
  onSetDiscount,
  onSetObservation,
  onProceedCheckout,
  checkoutButtonText = 'Ir para Pagamento',
  alertCalculate,
  setAlertCalculate,
  calculateInfo,
  setCalculateInfo,
}) => {
  const [showCustomerInput, setShowCustomerInput] = useState(false);
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [showObsInput, setShowObsInput] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [quantityModalItem, setQuantityModalItem] = useState(null);
  const lastClickTimeRef = useRef(0);

  const handleConfirmRemove = () => {
    if (itemToRemove !== null && onRemoveItem) {
      onRemoveItem(itemToRemove.index);
    }
    setItemToRemove(null);
  };

  const handleDecrement = (item, index) => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < 350) return;
    lastClickTimeRef.current = now;

    if (item.amount <= 0.1) {
      setItemToRemove({ index, name: item.name });
    } else if (item.amount < 1) {
      const next = Math.max(0.001, Math.round((item.amount - 0.1) * 1000) / 1000);
      if (next <= 0.001) {
        setItemToRemove({ index, name: item.name });
      } else if (onEditItem) {
        onEditItem(index, next);
      }
    } else if (item.amount === 1) {
      setItemToRemove({ index, name: item.name });
    } else if (onEditItem) {
      onEditItem(index, Math.round((item.amount - 1) * 1000) / 1000);
    }
  };

  const handleIncrement = (item, index) => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < 350) return;
    lastClickTimeRef.current = now;

    if (onEditItem) {
      onEditItem(index, Math.round((item.amount + 1) * 1000) / 1000);
    }
  };

  const handleSaveQuantityFromDrawer = (newQty) => {
    if (!quantityModalItem) return;
    const { index } = quantityModalItem;
    if (index !== null && index !== undefined && onEditItem) {
      onEditItem(index, newQty);
    }
  };

  const handleRemoveFromDrawerModal = () => {
    if (!quantityModalItem) return;
    const { index } = quantityModalItem;
    if (index !== null && index !== undefined && onRemoveItem) {
      onRemoveItem(index);
    }
  };

  const [couponCode, setCouponCode] = useState('');
  const [observation, setObservation] = useState(cartValues.observation || '');

  const currentDiscountValue = Number(
    manualDiscount !== undefined && manualDiscount !== null && manualDiscount !== 0
      ? manualDiscount
      : (cartValues.setDiscount !== undefined && cartValues.setDiscount !== null && cartValues.setDiscount !== ''
        ? cartValues.setDiscount
        : (cartValues.discount || 0))
  ) || 0;

  const [localDiscount, setLocalDiscount] = useState(currentDiscountValue);
  const [openClientModal, setOpenClientModal] = useState(false);
  const [clientInitialName, setClientInitialName] = useState('');

  // APIs
  const [customersLoad, customersInfo] = useApi({
    method: 'get',
    url: '/customers',
  });

  const [promotionsLoad, promotionsInfo] = useApi({
    method: 'get',
    url: '/promotions',
    params: { isActive: 1, isExpired: 0 },
  });

  useEffect(() => {
    if (open) {
      customersLoad();
      promotionsLoad();
    }
  }, [open]);

  useEffect(() => {
    setObservation(cartValues.observation || '');
  }, [cartValues?.observation]);

  useEffect(() => {
    setLocalDiscount(currentDiscountValue);
  }, [currentDiscountValue]);

  const totalItemsCount = listItens.reduce(
    (acc, item) => acc + Number(item.amount || 0),
    0
  );

  const totalItemsCountFormatted =
    totalItemsCount % 1 === 0
      ? totalItemsCount
      : Number(totalItemsCount.toFixed(3)).toLocaleString('pt-BR');

  const subtotal = listItens.reduce(
    (acc, item) => acc + Number(item.amount || 0) * Number(item.valueUn || 0),
    0
  );

  const finalTotal = cartValues.total || Math.max(0, subtotal - Number(manualDiscount || 0));

  // Mapa de itens calculados pela API para refletir descontos individuais por item
  const calculatedItemsMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(cartValues.products)) {
      cartValues.products.forEach((p) => {
        map.set(`product-${p.id}`, p);
      });
    }
    if (Array.isArray(cartValues.services)) {
      cartValues.services.forEach((s) => {
        map.set(`service-${s.id}`, s);
      });
    }
    return map;
  }, [cartValues.products, cartValues.services]);

  // Lista de promoções ativas e públicas/com cupom
  const availablePromotions = useMemo(() => {
    if (!promotionsInfo.data || !Array.isArray(promotionsInfo.data)) return [];
    return promotionsInfo.data.filter((p) => {
      if (p.id === 1) return false;
      const isActive = p.isActive === undefined || Boolean(Number(p.isActive));
      const notExpired = !p.isExpired;
      return isActive && notExpired;
    });
  }, [promotionsInfo.data]);

  // Aplicar Cupom digitado
  const handleApplyCouponCode = () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;

    const matchedPromo = availablePromotions.find(
      (p) => p.couponCode && p.couponCode.trim().toUpperCase() === code
    );

    if (matchedPromo) {
      if (onSelectPromotion) {
        onSelectPromotion(matchedPromo);
      }
      setCouponCode('');
      setShowPromoInput(false);
    } else if (onSelectPromotion) {
      // Força envio para a API retornar a validação de erro padrão se necessário
      onSelectPromotion({ id: 1, name: 'Sem promoção' });
    }
  };

  const handleSelectPromo = (promo) => {
    if (onSelectPromotion) {
      onSelectPromotion(promo);
    }
    setShowPromoInput(false);
  };

  const handleRemovePromo = () => {
    if (onSelectPromotion) {
      onSelectPromotion({ id: 1, name: 'Sem promoção' });
    }
    setShowPromoInput(false);
  };

  const handleApplyDiscount = (val) => {
    if (onSetDiscount) {
      onSetDiscount(val !== null && val !== undefined ? val : 0);
    }
  };

  const handleApplyObs = (val) => {
    setObservation(val);
    if (onSetObservation) {
      onSetObservation(val);
    }
  };

  const promoId = Number(
    typeof cartValues.promotion === 'object' && cartValues.promotion !== null
      ? cartValues.promotion.id
      : cartValues.promotion
  ) || 1;

  const isPromoApplied = promoId > 1;

  const promoName = useMemo(() => {
    if (!isPromoApplied) return 'Promoção ou Cupom';
    if (cartValues.promotion && typeof cartValues.promotion === 'object' && cartValues.promotion.name) {
      return cartValues.promotion.name;
    }
    const found = availablePromotions.find((p) => Number(p.id) === promoId);
    return found ? found.name : 'Promoção Ativa';
  }, [isPromoApplied, cartValues.promotion, availablePromotions, promoId]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: '90vh',
          backgroundColor: '#ffffff',
        },
      }}
    >
      <div className="sale-mobile-drawer-content">
        {/* Header da Gaveta */}
        <div className="sale-mobile-drawer-header">
          <div className="sale-mobile-drawer-title">
            <ShoppingCartIcon style={{ color: '#17497f', fontSize: 20 }} />
            <span>Carrinho de Compras</span>
            <Chip
              size="small"
              label={`${totalItemsCountFormatted} ${totalItemsCount === 1 ? 'item' : 'itens'}`}
              style={{ backgroundColor: '#e2e8f0', fontWeight: 700, fontSize: '0.75rem' }}
            />
          </div>
          <IconButton size="small" onClick={onClose} style={{ color: '#64748b' }}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Corpo com a Lista de Itens */}
        <div className="sale-mobile-drawer-body">
          {listItens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <ShoppingCartIcon style={{ fontSize: 44, color: '#cbd5e1', marginBottom: 8 }} />
              <div style={{ fontWeight: 600, color: '#1e293b' }}>Seu carrinho está vazio</div>
              <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
                Adicione produtos ou serviços pelo catálogo.
              </div>
            </div>
          ) : (
            listItens.map((item, index) => {
              const originalTotal = Number(item.amount || 0) * Number(item.valueUn || 0);
              const calcItem = calculatedItemsMap.get(`${item.typeItem || 'product'}-${item.id}`);
              const hasDiscount = Boolean(calcItem && Number(calcItem.discount) > 0);
              const finalItemTotal = calcItem && typeof calcItem.total === 'number' ? calcItem.total : originalTotal;
              const itemDiscount = calcItem ? Number(calcItem.discount || 0) : 0;

              return (
                <div key={`${item.typeItem}-${item.id}-${index}`} className="sale-mobile-cart-item">
                  <div className="sale-mobile-item-details">
                    <div className="sale-mobile-item-name">{item.name}</div>
                    <div className="sale-mobile-item-price">
                      {formattedCurrency(item.valueUn)} un. •{' '}
                      {hasDiscount ? (
                        <>
                          <span className="sale-mobile-item-total" style={{ color: '#16a34a', fontWeight: 700 }}>
                            Total: {formattedCurrency(finalItemTotal)}
                          </span>
                          <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.75rem', marginLeft: 6 }}>
                            {formattedCurrency(originalTotal)}
                          </span>
                          <span
                            style={{
                              backgroundColor: '#dcfce7',
                              color: '#15803d',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              padding: '1px 5px',
                              borderRadius: 4,
                              marginLeft: 6,
                            }}
                          >
                            -{formattedCurrency(itemDiscount)}
                          </span>
                        </>
                      ) : (
                        <span className="sale-mobile-item-total">
                          Total: {formattedCurrency(originalTotal)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div className="sale-mobile-stepper">
                      <button
                        type="button"
                        className="sale-mobile-stepper-btn"
                        onClick={() => handleDecrement(item, index)}
                        aria-label="Diminuir quantidade"
                      >
                        <RemoveIcon style={{ fontSize: 15 }} />
                      </button>
                      <span
                        className="sale-mobile-stepper-val"
                        onClick={() => setQuantityModalItem({ item, index, currentAmount: item.amount })}
                        title="Toque para definir quantidade fracionada"
                      >
                        {Number(item.amount).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                      </span>
                      <button
                        type="button"
                        className="sale-mobile-stepper-btn"
                        onClick={() => handleIncrement(item, index)}
                        aria-label="Aumentar quantidade"
                      >
                        <AddIcon style={{ fontSize: 15 }} />
                      </button>
                    </div>

                    <IconButton
                      size="small"
                      onClick={() => {
                        const now = Date.now();
                        if (now - lastClickTimeRef.current < 250) return;
                        lastClickTimeRef.current = now;
                        setItemToRemove({ index, name: item.name });
                      }}
                      style={{
                        color: '#ef4444',
                        backgroundColor: '#fee2e2',
                        padding: 6,
                        borderRadius: 8,
                      }}
                      aria-label="Excluir item"
                    >
                      <DeleteOutlineIcon style={{ fontSize: 18 }} />
                    </IconButton>
                  </div>
                </div>
              );
            })
          )}

          {/* Seções de Ajustes Rápidos (Cliente, Promoções/Cupom, Desconto, Observação) */}
          <div style={{ marginTop: 16, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
            {/* 1. Seletor de Cliente */}
            <div style={{ marginBottom: 10 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  backgroundColor: '#f8fafc',
                  borderRadius: 10,
                  cursor: 'pointer',
                  border: '1px solid #e2e8f0',
                }}
                onClick={() => setShowCustomerInput(!showCustomerInput)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <PersonOutlineIcon style={{ color: '#17497f', fontSize: 20 }} />
                  <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#334155' }}>
                    Cliente:{' '}
                    {cartValues.customer?.name
                      ? `${cartValues.customer.name} ${cartValues.customer.lastName || cartValues.customer.last_name || ''}`.trim()
                      : 'Consumidor Final'}
                  </span>
                </div>
                <span style={{ fontSize: '0.75rem', color: '#17497f', fontWeight: 700 }}>
                  {showCustomerInput ? 'Fechar' : 'Alterar'}
                </span>
              </div>

              <Collapse in={showCustomerInput}>
                <Box mt={1}>
                  <Autocomplete
                    options={customersInfo.data || []}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      const fullName = `${option.name || ''} ${option.lastName || option.last_name || ''}`.trim();
                      return `${fullName || option.name} ${option.cpf ? `(${option.cpf})` : ''}`.trim();
                    }}
                    getOptionSelected={(option, value) => option?.id === value?.id}
                    filterOptions={(options, state) => {
                      const term = normalizeText(state.inputValue);
                      const filtered = !term
                        ? [...options]
                        : options.filter((option) => {
                            const fullName = normalizeText(`${option.name || ''} ${option.lastName || option.last_name || ''}`);
                            const cpf = normalizeText(option.cpf);
                            const phone = normalizeText(option.phone || option.whatsapp);
                            const id = String(option.id);
                            return fullName.includes(term) || cpf.includes(term) || phone.includes(term) || id.includes(term);
                          });

                      if (state.inputValue && state.inputValue.trim()) {
                        const exactMatch = filtered.some(
                          (opt) => normalizeText(`${opt.name || ''} ${opt.lastName || ''}`).trim() === term
                        );
                        if (!exactMatch) {
                          filtered.push({
                            id: '__create_new_client__',
                            isCreateNew: true,
                            name: state.inputValue.trim(),
                          });
                        }
                      }
                      return filtered;
                    }}
                    renderOption={(option) => {
                      if (option.isCreateNew) {
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', color: '#17497f', fontWeight: 600 }}>
                            <PersonAddIcon style={{ fontSize: 18 }} />
                            <span>+ Cadastrar novo: "{option.name}"</span>
                          </div>
                        );
                      }
                      const fullName = `${option.name || ''} ${option.lastName || option.last_name || ''}`.trim();
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', padding: '2px 0' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }}>
                            {fullName || option.name}
                          </span>
                          {(option.cpf || option.phone || option.whatsapp) && (
                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              {[option.cpf ? `CPF: ${option.cpf}` : null, option.phone || option.whatsapp].filter(Boolean).join(' • ')}
                            </span>
                          )}
                        </div>
                      );
                    }}
                    value={cartValues.customer || null}
                    onChange={(event, newValue) => {
                      if (newValue && newValue.isCreateNew) {
                        setClientInitialName(newValue.name);
                        setOpenClientModal(true);
                        return;
                      }
                      if (onSelectCustomer) {
                        onSelectCustomer(newValue);
                      }
                      setShowCustomerInput(false);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Pesquisar cliente"
                        variant="outlined"
                        size="small"
                        placeholder="Nome completo, CPF..."
                      />
                    )}
                  />

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                    <Button
                      size="small"
                      color="primary"
                      startIcon={<PersonAddIcon style={{ fontSize: 15 }} />}
                      onClick={() => {
                        setClientInitialName('');
                        setOpenClientModal(true);
                      }}
                      style={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 700, color: '#17497f' }}
                    >
                      + Cadastrar Novo Cliente
                    </Button>
                  </div>
                </Box>
              </Collapse>
            </div>

            {/* 2. Promoções e Cupom de Desconto */}
            <div style={{ marginBottom: 10 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  backgroundColor: isPromoApplied ? '#f0fdf4' : '#f8fafc',
                  borderRadius: 10,
                  cursor: 'pointer',
                  border: `1px solid ${isPromoApplied ? '#86efac' : '#e2e8f0'}`,
                }}
                onClick={() => setShowPromoInput(!showPromoInput)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LoyaltyIcon style={{ color: isPromoApplied ? '#16a34a' : '#ea580c', fontSize: 20 }} />
                  <span style={{ fontSize: '0.88rem', fontWeight: 600, color: isPromoApplied ? '#15803d' : '#334155' }}>
                    {isPromoApplied ? promoName : 'Promoção ou Cupom'}
                  </span>
                </div>
                <span style={{ fontSize: '0.75rem', color: isPromoApplied ? '#16a34a' : '#ea580c', fontWeight: 700 }}>
                  {showPromoInput ? 'Fechar' : (isPromoApplied ? 'Alterar' : 'Aplicar')}
                </span>
              </div>

              <Collapse in={showPromoInput}>
                <Box mt={1.5} p={1.5} style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                  {/* Inserir Código de Cupom */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <TextField
                      label="Código do Cupom"
                      variant="outlined"
                      size="small"
                      placeholder="Ex: PROMO10"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      style={{ flex: 1 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleApplyCouponCode}
                      style={{ backgroundColor: '#17497f', textTransform: 'none', fontWeight: 700 }}
                    >
                      Aplicar
                    </Button>
                  </div>

                  {/* Lista de Promoções Disponíveis */}
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                    Promoções disponíveis:
                  </div>

                  {promotionsInfo.loading ? (
                    <div style={{ textAlign: 'center', padding: 12 }}>
                      <CircularProgress size={20} />
                    </div>
                  ) : availablePromotions.length === 0 ? (
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', textAlign: 'center', padding: 8 }}>
                      Nenhuma outra promoção ativa no momento.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {availablePromotions.map((promo) => {
                        const isSelected = promoId === Number(promo.id);
                        return (
                          <div
                            key={promo.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 10px',
                              borderRadius: 8,
                              backgroundColor: isSelected ? '#dcfce7' : '#ffffff',
                              border: `1px solid ${isSelected ? '#86efac' : '#cbd5e1'}`,
                              cursor: 'pointer',
                            }}
                            onClick={() => handleSelectPromo(promo)}
                          >
                            <div>
                              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                                {promo.name}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                {Number(promo?.percentage) > 0 ? `${promo.percentage}% OFF` : (Number(promo?.discount) > 0 ? `${formattedCurrency(promo.discount)} de desconto` : '')}
                                {promo.minValue ? ` • Mínimo ${formattedCurrency(promo.minValue)}` : ''}
                              </div>
                            </div>
                            {isSelected ? (
                              <CheckCircleIcon style={{ color: '#16a34a', fontSize: 18 }} />
                            ) : (
                              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#17497f' }}>
                                Usar
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {isPromoApplied && (
                    <Button
                      fullWidth
                      size="small"
                      onClick={handleRemovePromo}
                      style={{ marginTop: 10, color: '#ef4444', textTransform: 'none', fontWeight: 600 }}
                    >
                      Remover promoção aplicada
                    </Button>
                  )}
                </Box>
              </Collapse>
            </div>

            {/* 3. Desconto Manual (Desativado caso haja promoção selecionada que não seja a 1) */}
            <div style={{ marginBottom: 10 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  backgroundColor: isPromoApplied ? '#f1f5f9' : '#f8fafc',
                  borderRadius: 10,
                  cursor: isPromoApplied ? 'not-allowed' : 'pointer',
                  border: `1px solid ${isPromoApplied ? '#cbd5e1' : '#e2e8f0'}`,
                  opacity: isPromoApplied ? 0.65 : 1,
                }}
                onClick={() => {
                  if (!isPromoApplied) {
                    setShowDiscountInput(!showDiscountInput);
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LocalOfferOutlinedIcon style={{ color: isPromoApplied ? '#94a3b8' : '#2e7d32', fontSize: 20 }} />
                  <span style={{ fontSize: '0.88rem', fontWeight: 600, color: isPromoApplied ? '#64748b' : '#334155' }}>
                    Desconto Manual: {currentDiscountValue > 0 ? formattedCurrency(currentDiscountValue) : 'Nenhum'}
                  </span>
                </div>
                <span style={{ fontSize: '0.75rem', color: isPromoApplied ? '#94a3b8' : '#2e7d32', fontWeight: 700 }}>
                  {isPromoApplied ? 'Bloqueado' : (showDiscountInput ? 'Fechar' : 'Aplicar')}
                </span>
              </div>

              <Collapse in={showDiscountInput && !isPromoApplied}>
                <Box mt={1}>
                  <CurrencyTextField
                    label="Valor do Desconto Manual"
                    variant="outlined"
                    size="small"
                    disabled={isPromoApplied}
                    value={localDiscount > 0 ? localDiscount : ''}
                    currencySymbol="R$"
                    outputFormat="number"
                    decimalCharacter=","
                    digitGroupSeparator="."
                    onChange={(event, value) => {
                      setLocalDiscount(value !== null && value !== undefined ? value : 0);
                    }}
                    onBlur={() => {
                      if (Number(localDiscount || 0) !== Number(currentDiscountValue || 0)) {
                        handleApplyDiscount(localDiscount);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    helperText={isPromoApplied ? 'Desconto manual indisponível com promoção selecionada.' : ''}
                    fullWidth
                  />
                </Box>
              </Collapse>
            </div>

            {/* 4. Observações */}
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  backgroundColor: '#f8fafc',
                  borderRadius: 10,
                  cursor: 'pointer',
                  border: '1px solid #e2e8f0',
                }}
                onClick={() => setShowObsInput(!showObsInput)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <NotesIcon style={{ color: '#64748b', fontSize: 20 }} />
                  <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#334155' }}>
                    Observações
                  </span>
                </div>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 700 }}>
                  {showObsInput ? 'Fechar' : 'Adicionar'}
                </span>
              </div>

              <Collapse in={showObsInput}>
                <Box mt={1}>
                  <TextField
                    label="Observação da Venda"
                    variant="outlined"
                    size="small"
                    multiline
                    rows={2}
                    value={observation}
                    onChange={(e) => setObservation(e.target.value)}
                    onBlur={() => {
                      if (observation !== cartValues.observation) {
                        handleApplyObs(observation);
                      }
                    }}
                    fullWidth
                  />
                </Box>
              </Collapse>
            </div>
          </div>
        </div>

        {/* Rodapé da Gaveta com Totais e Botão de Ação */}
        <div className="sale-mobile-drawer-footer">
          <div className="sale-mobile-drawer-summary-row">
            <span>Subtotal:</span>
            <span>{formattedCurrency(subtotal)}</span>
          </div>
          {Number(cartValues.discount || manualDiscount) > 0 && (
            <div className="sale-mobile-drawer-summary-row" style={{ color: '#2e7d32', fontWeight: 600 }}>
              <span>Desconto:</span>
              <span>-{formattedCurrency(cartValues.discount || manualDiscount)}</span>
            </div>
          )}
          <div className="sale-mobile-drawer-total-row">
            <span>Total:</span>
            <span>{formattedCurrency(finalTotal)}</span>
          </div>

          <button
            type="button"
            className="sale-mobile-checkout-btn"
            disabled={listItens.length === 0}
            onClick={() => {
              onClose();
              if (onProceedCheckout) {
                onProceedCheckout();
              }
            }}
          >
            <span>{checkoutButtonText}</span>
            <ArrowForwardIosIcon style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão de Item */}
      <Dialog
        open={Boolean(itemToRemove)}
        onClose={() => setItemToRemove(null)}
        PaperProps={{
          style: {
            borderRadius: 14,
            padding: '8px',
            maxWidth: '360px',
          },
        }}
      >
        <DialogTitle style={{ fontWeight: 700, paddingBottom: 4 }}>
          Remover item?
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ fontSize: '0.9rem', color: '#475569' }}>
            Tem certeza que deseja remover <strong>"{itemToRemove?.name}"</strong> da comanda?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '8px 16px 12px 16px' }}>
          <Button
            onClick={() => setItemToRemove(null)}
            color="default"
            style={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmRemove}
            variant="contained"
            style={{
              backgroundColor: '#ef4444',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Quantidade Fracionada */}
      <SaleMobileQuantityModal
        open={Boolean(quantityModalItem)}
        onClose={() => setQuantityModalItem(null)}
        item={quantityModalItem?.item}
        currentAmount={quantityModalItem?.currentAmount || 1}
        onConfirm={handleSaveQuantityFromDrawer}
        onRemove={handleRemoveFromDrawerModal}
      />

      <ClientCreateModal
        open={openClientModal}
        onClose={() => setOpenClientModal(false)}
        initialName={clientInitialName}
        onSuccess={(newCustomer) => {
          if (customersInfo.data && Array.isArray(customersInfo.data)) {
            customersInfo.data.unshift(newCustomer);
          }
          if (onSelectCustomer) {
            onSelectCustomer(newCustomer);
          }
          setOpenClientModal(false);
          setShowCustomerInput(false);
        }}
      />
    </Drawer>
  );
};

export default SaleMobileCartDrawer;
